import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useProAuth } from '@/lib/ProAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Loader2, CheckCircle, Crown, Home, Send, XCircle, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PRO Subscription Success Page
 * Polls payment status and shows confirmation
 */
export const ProSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, isAuthenticated } = useProAuth();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [polling, setPolling] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/pro-subscription/checkout/status/${sessionId}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error('Failed to check status');
        }

        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPolling(false);
          // Refresh user to get updated subscription
          await refreshUser();
        } else if (data.status === 'expired') {
          setStatus('error');
          setPolling(false);
        }
        // Continue polling if still pending
      } catch (error) {
        console.error('Status poll error:', error);
      }
    };

    // Initial poll
    pollStatus();

    // Poll every 2 seconds for 30 seconds
    const interval = setInterval(() => {
      if (polling) pollStatus();
    }, 2000);

    const timeout = setTimeout(() => {
      setPolling(false);
      if (status === 'checking') {
        // If still checking after 30s, assume success (webhook will handle)
        setStatus('success');
        refreshUser();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [sessionId, polling, status, refreshUser]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Confirming Payment...</h2>
            <p className="text-gray-600">Please wait while we verify your subscription.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 border-2 border-green-200" data-testid="success-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">
              Welcome to PRO!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-orange-700 font-medium mb-2">
                <Crown className="h-5 w-5 text-yellow-600" />
                <span>Your PRO subscription is active</span>
              </div>
              <p className="text-sm text-orange-600">
                You now have access to Quick Response and all PRO features!
              </p>
            </div>

            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full bg-orange-500 hover:bg-orange-600" data-testid="new-request-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Create Service Request
                </Button>
              </Link>
              <Link to="/pro/dashboard">
                <Button variant="outline" className="w-full">
                  <Home className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4 border-2 border-red-200">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-xl text-red-800">
            Payment Issue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-600">
            We couldn't verify your payment. This may be temporary - please check your email for confirmation.
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/pro/dashboard">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * PRO Subscription Cancel Page
 * Shown when user cancels checkout
 */
export const ProCancelPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl">Checkout Cancelled</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-600">
            No worries! You can upgrade to PRO anytime to access Quick Response and faster provider replies.
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/pro/dashboard">
              <Button className="w-full bg-orange-500 hover:bg-orange-600">
                <Crown className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                <Home className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProSuccessPage;
